import {
    CreditStudentCoinsForm,
    CreditStudentCoinsFormValues,
} from 'src/forms/CreditStudentCoins';
import {
    DebitStudentCoinsForm,
    DebitStudentCoinsFormValues,
} from 'src/forms/DebitStudentCoins';
import { SecondaryButton } from 'src/components/design-system/form/buttons/SecondaryButton';
import { PrimaryButton } from 'src/components/design-system/form/buttons/PrimaryButton';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { ApplicationModal } from 'src/components/Modal/LegacyModal';
import { LogoIcon } from 'src/icons/Logo';
import React, { useState } from 'react';
import { Drawer, Skeleton } from 'antd';
import classNames from 'classnames';
import orderBy from 'lodash/orderBy';
import map from 'lodash/map';
import dayjs from 'dayjs';
import './module.scss';
import {
    BalanceTransaction,
    useCreditStudentCoinBalanceMutation,
    useDebitStudentCoinBalanceMutation,
    useGetStudentCoinBalanceQuery,
    useGetStudentCoinBalanceTransactionsQuery,
} from 'src/api/endpoints/student-coins-balance/api';

export type StudentCoinBalanceWidgetProps = {
    studentId: number;
};

export const StudentCoinBalanceWidget = (
    props: StudentCoinBalanceWidgetProps
) => {
    const { studentId } = props;

    const [isSidebarOpen, setSidebarOpen] = useState(false);

    const [isDebitModalOpen, setDebitModalOpen] = useState(false);
    const [isCreditModalOpen, setCreditModalOpen] = useState(false);

    const { data: balance, isLoading: isBalanceLoading } =
        useGetStudentCoinBalanceQuery({ params: { studentId: studentId } });

    const [debitStudentBalance] = useDebitStudentCoinBalanceMutation();
    const [creditStudentBalance] = useCreditStudentCoinBalanceMutation();

    const onDebitStudentCoinBalance = async (
        values: DebitStudentCoinsFormValues
    ): Promise<void> => {
        await debitStudentBalance({
            params: { studentId: studentId },
            body: values,
        })
            .unwrap()
            .then(() => {
                notifyAboutSuccess({
                    content: 'Средства успешно списаны с баланса студента',
                    sound: false,
                });
                setDebitModalOpen(false);
            })
            .catch(res => {
                notifyAboutException({
                    content: res.message,
                    sound: true,
                });
            });
    };

    const onCreditStudentCoinBalance = async (
        values: CreditStudentCoinsFormValues
    ): Promise<void> => {
        await creditStudentBalance({
            params: { studentId: studentId },
            body: values,
        })
            .unwrap()
            .then(() => {
                notifyAboutSuccess({
                    content: 'Средства успешно начислены на баланс студента',
                });
                setCreditModalOpen(false);
            })
            .catch(res => {
                notifyAboutException({
                    content: res.message,
                    sound: true,
                });
            });
    };

    const {
        data: balanceTransactions,
        // isLoading: isBalanceTransactionsLoading,
    } = useGetStudentCoinBalanceTransactionsQuery({
        params: { studentId: studentId },
    });

    return (
        <>
            {isBalanceLoading || balance === undefined ? (
                <Skeleton.Input
                    active={true}
                    style={{
                        width: '70px',
                        minWidth: '70px',
                        height: '100%',
                    }}
                />
            ) : (
                <button
                    type={'button'}
                    className={classNames('student-coin-balance', 'coin')}
                    onClick={() => setSidebarOpen(true)}
                >
                    <LogoIcon size={15} color={'#FFFFFF'} />
                    <p>{balance}</p>
                </button>
            )}

            <ApplicationModal
                isOpen={isDebitModalOpen}
                onClose={() => setDebitModalOpen(false)}
                header={'Списание монет'}
            >
                <DebitStudentCoinsForm
                    studentId={studentId}
                    onCancel={() => setDebitModalOpen(false)}
                    onSubmit={onDebitStudentCoinBalance}
                />
            </ApplicationModal>

            <ApplicationModal
                isOpen={isCreditModalOpen}
                onClose={() => setCreditModalOpen(false)}
                header={'Начисление монет'}
            >
                <CreditStudentCoinsForm
                    studentId={studentId}
                    onCancel={() => setCreditModalOpen(false)}
                    onSubmit={onCreditStudentCoinBalance}
                />
            </ApplicationModal>

            <Drawer
                open={isSidebarOpen}
                onClose={() => setSidebarOpen(false)}
                closable={true}
                classNames={{
                    wrapper: classNames('student-coin-balance-sidebar-wrapper'),
                    content: classNames('student-coin-balance-sidebar'),
                }}
            >
                <div className={'widget-content'}>
                    <section className={'widget-section student-balance'}>
                        <div className={'section-header'}>Баланс студента</div>
                        <div className={'section-content'}>
                            {isBalanceLoading || balance === undefined ? (
                                <Skeleton.Input
                                    active={true}
                                    block={true}
                                    style={{
                                        height: '100px',
                                        width: '100%',
                                        borderRadius: '10px',
                                    }}
                                />
                            ) : (
                                <div className={'coins-container'}>
                                    <div className="balance-name">
                                        Цифракоины
                                    </div>

                                    <div className="balance-value">
                                        <div className={'balance-quantity'}>
                                            {balance
                                                .toFixed(2)
                                                .replace('.', ',')}
                                        </div>
                                        <LogoIcon
                                            className={'balance-units'}
                                            color={'#FFFFFF'}
                                            size={20}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className={'coins-actions'}>
                                <SecondaryButton
                                    autoFocus={true}
                                    className={'action-button'}
                                    label={'Начислить'}
                                    onClick={() => setCreditModalOpen(true)}
                                />

                                <PrimaryButton
                                    className={'action-button'}
                                    label={'Списать'}
                                    disabled={balance === 0}
                                    onClick={() => setDebitModalOpen(true)}
                                />
                            </div>
                        </div>
                    </section>
                    <section className="widget-section balance-transactions">
                        <div className={'section-header'}>История операций</div>
                        <div className={'section-content'}>
                            <div className="transactions">
                                {map(
                                    orderBy(
                                        balanceTransactions,
                                        'createdAt',
                                        'desc'
                                    ),
                                    (transaction: BalanceTransaction) => {
                                        return (
                                            <div
                                                key={transaction.id}
                                                id={transaction.id}
                                                className={'transaction'}
                                                style={{
                                                    minHeight: '60px',
                                                    height: 'fit-content',
                                                    display: 'grid',
                                                    padding: '10px 0',
                                                    gridTemplateColumns:
                                                        '5fr 16fr 4fr',
                                                    borderBottom:
                                                        '1px solid #dadee0',
                                                }}
                                            >
                                                <div
                                                    data-created-at={
                                                        transaction.createdAt
                                                    }
                                                    className="transaction-created-timestamp"
                                                    style={{
                                                        padding: '0 12px 0 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'start',
                                                        fontSize: '14px',
                                                        gridColumn: 1,
                                                    }}
                                                >
                                                    {dayjs(
                                                        transaction.createdAt
                                                    ).format(
                                                        'DD.MM.YYYY HH:mm'
                                                    )}
                                                </div>
                                                <div
                                                    className="transaction-description"
                                                    style={{
                                                        width: '100%',
                                                        padding: '0 12px',
                                                        fontSize: '14px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'start',
                                                        gridColumn: 2,
                                                    }}
                                                >
                                                    {transaction.description}
                                                </div>
                                                <div
                                                    className="transaction-amount"
                                                    style={{
                                                        width: '100%',
                                                        padding: '0 0 0 12px',
                                                        fontSize: '14px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'end',
                                                        gridColumn: 3,
                                                        color:
                                                            transaction.amount >
                                                            0
                                                                ? '#309166'
                                                                : 'inherit',
                                                    }}
                                                >
                                                    {(transaction.amount > 0
                                                        ? '+'
                                                        : '') +
                                                        transaction.amount
                                                            .toFixed(2)
                                                            .replace('.', ',')}
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </Drawer>
        </>
    );
};
